import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AcademyCoachList.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useLocation } from 'react-router-dom';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const AcademyCoachList = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('auth-token');
  const { selectedCenter} = useUser();
  const queryParams = new URLSearchParams(location.search);
  const academyIdFromQuery = queryParams.get("academyId");
  const { academy } = location.state || {};
  const centerToUse = academy?.id || academyIdFromQuery || selectedCenter;


  const fetchCoachesData = async () => {
    setLoading(true)
    setError('')
    if (centerToUse) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ACADEMY_COACHES}/${centerToUse}/coaches`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        if (result.success) {
          const fetchedPlayingMemberStatus = result.data.playingMember;
          const updatedCoaches = result.data.coaches.map((coach) => {
            const coachDetails = {
              id: coach.id,
              name: coach.user.name || 'Unknown',
              about: coach.about || 'No description available',
              experience: coach.experienceMonths
                ? `${Math.floor(coach.experienceMonths / 12)} years ${coach.experienceMonths % 12} months`
                : 'No experience available',

              playingMember: fetchedPlayingMemberStatus[coach.id] ? 'Yes' : 'No',
              mobileNumber: coach.user.mobile_number || 'Unknown',
              profilePicUrl: coach.user.profile_pic_url || null,
            };
            return coachDetails;
          });

          setCoaches(updatedCoaches);
        }
      } catch (error) {
        setError('Failed to load data');
      }
      finally {
        setLoading(false);
      }
    };


  }
  useEffect(() => {
    fetchCoachesData();
  }, [academy, selectedCenter]);

  const headers = [
    { label: "Name", key: "name" },
    { label: "About", key: "about" },
    { label: "Experience", key: "experience" },
    { label: "Playing Member", key: "playingMember" },
  ];

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredCoaches = coaches.filter((coach) =>
    Object.values(coach)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });
  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;

    return [...data].sort((a, b) => {
      let valueA = a[key] ?? "";
      let valueB = b[key] ?? "";

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      return direction === "ascending" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };
  const sortedData = sortData(filteredCoaches);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredCoaches.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  return (
    <div className="enrollment-container">
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchCoachesData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredCoaches.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((coach) => (
              <tr key={coach.id}>
                <td>{coach.name}</td>
                <td>{coach.about}</td>
                <td>{coach.experience}</td>
                <td>{coach.playingMember}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AcademyCoachList;
